import React, { useCallback, useEffect, useState } from "react";
import "../../../App.css";

// import TelegramHeader from "../../components/kit/Header/TelegramHeader";
import TelegramText from "../../../components/kit/Text/TelegramText";
import { useTelegram } from "../../../hooks/useTelegram";
import { useNavigate } from "react-router-dom";
import {
  PATH_CATEGORY,
  PATH_CHECKOUT,
  PATH_ORDER_REVIEW,
} from "../../../constants/Paths";
import TelegramScreen from "../../../components/kit/Screen/TelegramScreen";
import TelegramDetailedButton from "../../../components/kit/DetailedButton/TelegramDetailedButton";
import Card from "../../../components/kit/Card/Card";
// import Cart from "../../components/kit/Cart/Cart";
import TelegramHeader from "../../../components/kit/Header/TelegramHeader";
import StoreIndicator from "../../../components/kit/StoreIndicator/StoreIndicator";
import Breadcrumb from "../../../components/kit/Breadcrumb/Breadcrumb";
import { useDispatch, useSelector } from "react-redux";
import {
  addToCart,
  removeFromCart,
  replaceCart,
} from "../../../components/kit/Redux/Actions";
import ProductSkeleton from "../../../components/kit/Skeleton/ProductSkeleton";

const ProductScreen = () => {
  const { webApp } = useTelegram();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const selectedCategory = useSelector((state) => state.selectedCategory);
  const selectedBrand = useSelector((state) => state.selectedBrand);
  const selectedProductType = useSelector((state) => state.selectedProductType);
  const products = useSelector((state) => state.products);
  const cartItems = useSelector((state) => state.cartItems.cartItems);
  const existingOrder = useSelector((state) => state.existingOrder);

  const onMainClick = useCallback(() => {
    const sortedCartItems = [...cartItems].sort((a, b) => {
      // Sort by Type
      const typeResult = a.type.localeCompare(b.type);
      if (typeResult !== 0) return typeResult;

      // Sort by Brand
      const brandResult = a.brand.localeCompare(b.brand);
      if (brandResult !== 0) return brandResult;

      // Sort by Category
      const categoryResult = a.category.localeCompare(b.category);
      if (categoryResult !== 0) return categoryResult;

      // Sort by Title
      return a.title.localeCompare(b.title);
    });

    dispatch(replaceCart(sortedCartItems));

    // alert(`View Trolley Clicked: ${JSON.stringify(sortedCartItems)}`);

    if (existingOrder) {
      navigate(PATH_ORDER_REVIEW, { replace: true });
    } else {
      navigate(PATH_CHECKOUT, { replace: true });
    }
  }, [navigate, cartItems, dispatch]);

  const onBackClick = useCallback(() => {
    navigate(PATH_CATEGORY, { replace: true });
  }, [navigate]);

  useEffect(() => {
    webApp.ready();
    webApp.MainButton.onClick(onMainClick);
    webApp.BackButton.onClick(onBackClick);

    webApp.MainButton.text = "VIEW TROLLEY";
    // webApp.MainButton.color = "#1c54bd";
    webApp.MainButton.color = "#31b545";

    if (cartItems.length > 0) {
      webApp.MainButton.show();
    } else {
      webApp.MainButton.hide();
    }

    return () => {
      webApp.MainButton.offClick(onMainClick);
      webApp.BackButton.offClick(onBackClick);
    };
  }, [webApp, cartItems, onMainClick, onBackClick]);

  const onAdd = (item, newAddQty) => {
    dispatch(addToCart(item, newAddQty));
  };

  const onRemove = (item, newSubQty) => {
    dispatch(removeFromCart(item, newSubQty));
  };

  // Prepare breadcrumb items
  const breadcrumbItems = [];
  if (selectedProductType) {
    breadcrumbItems.push({
      title: selectedProductType?.title || selectedProductType,
      level: "type",
    });
  }
  if (selectedBrand) {
    breadcrumbItems.push({
      title: selectedBrand?.title || selectedBrand,
      level: "brand",
    });
  }
  if (selectedCategory) {
    breadcrumbItems.push({
      title: selectedCategory?.title || selectedCategory,
      level: "category",
    });
  }

  // Calculate total cart quantity for order-level limit
  const totalCartQuantity = Array.isArray(cartItems)
    ? cartItems.reduce((sum, item) => sum + item.quantity, 0)
    : 0;

  // Check max order quantity limit
  const getMaxOrderQuantityConfig = () => {
    try {
      const config = localStorage.getItem("maxOrderQuantityConfig");
      return config
        ? JSON.parse(config)
        : { enabled: false, maxOrderQuantity: 0 };
    } catch (error) {
      return { enabled: false, maxOrderQuantity: 0 };
    }
  };

  const maxOrderConfig = getMaxOrderQuantityConfig();
  // Only show banner when we've actually hit the limit (can't add more)
  const isOrderLimitReached =
    maxOrderConfig.enabled &&
    maxOrderConfig.maxOrderQuantity > 0 &&
    totalCartQuantity >= maxOrderConfig.maxOrderQuantity;

  // Check if we're loading (empty array) or have actual products
  const isLoadingProducts = !products || products.length === 0;

  // Filter available products (only used when we have actual data)
  const availableProducts =
    products && products.length > 0
      ? products.filter((item) => item.availableQty > 0)
      : [];

  // Only show "sold out" if we have products array but all are unavailable
  const hasNoAvailableProducts =
    products && products.length > 0 && availableProducts.length === 0;

  return (
    <TelegramScreen showbackbutton={1}>
      <StoreIndicator />
      <Breadcrumb items={breadcrumbItems} currentLevel="product" />
      <div>
        <TelegramHeader>
          <TelegramText className={"telegramTitle"}>
            {selectedCategory?.title || selectedCategory}
          </TelegramText>
        </TelegramHeader>
        {isLoadingProducts ? (
          <div className="cards__container">
            <ProductSkeleton count={6} />
          </div>
        ) : hasNoAvailableProducts ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "200px",
              padding: "20px",
              textAlign: "center",
            }}
          >
            <TelegramText style={{ color: "#999", fontSize: "16px" }}>
              Products sold out while you were shopping...
            </TelegramText>
          </div>
        ) : (
          <div className="cards__container">
            {availableProducts.map((item) => {
              const itemInCart = Array.isArray(cartItems)
                ? cartItems.find((cartItem) => cartItem.id === item.id)
                : null;
              const currentCount = itemInCart ? itemInCart.quantity : 0;
              return (
                <Card
                  food={item}
                  key={item.id}
                  currentCount={currentCount}
                  totalCartQuantity={totalCartQuantity}
                  onAdd={onAdd}
                  onRemove={onRemove}
                />
              );
            })}
          </div>
        )}
      </div>
      {isOrderLimitReached && (
        <div
          style={{
            position: "fixed",
            bottom: "16px",
            left: "16px",
            right: "16px",
            backgroundColor: "#fff3cd",
            color: "#856404",
            padding: "10px 16px",
            borderRadius: "8px",
            fontSize: "14px",
            fontWeight: "600",
            textAlign: "center",
            border: "1px solid #ffc107",
            zIndex: 999,
          }}
        >
          Order limit reached ({maxOrderConfig.maxOrderQuantity} items).
          Please proceed to checkout.
        </div>
      )}
    </TelegramScreen>
  );
};

export default ProductScreen;
